/** Detect Tezam/CMS HTML blobs vs plain text. */
export function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

const ALLOWED_TAGS = new Set([
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "ul",
  "ol",
  "li",
  "span",
  "div",
  "a",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
]);

const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(["href", "title", "target", "rel"]),
};

function isSafeHref(href: string): boolean {
  const v = href.trim().toLowerCase();
  return (
    v.startsWith("http:") ||
    v.startsWith("https:") ||
    v.startsWith("mailto:") ||
    v.startsWith("tel:") ||
    v.startsWith("/") ||
    v.startsWith("#")
  );
}

/**
 * Kentico stores site links as `~/www.example.com/path` — turn into https URLs.
 * Bare `~/local/path` without a host cannot be resolved here and is left as-is (then dropped).
 */
export function normalizeKenticoHref(href: string): string {
  const h = href.trim();
  if (!h) return h;
  const tildeHost = /^~\/((?:www\.)?[^/\s]+\.[a-z0-9.-]+)(\/.*)?$/i.exec(h);
  if (tildeHost) {
    const host = tildeHost[1];
    const path = tildeHost[2] ?? "";
    return `https://${host}${path}`;
  }
  if (h.startsWith("//")) return `https:${h}`;
  return h;
}

/**
 * Strip scripts/handlers/inline styles from CMS HTML so it can be rendered safely.
 * Browser-only (DOMParser).
 */
export function sanitizeHtml(html: string): string {
  if (typeof DOMParser === "undefined") {
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
  const doc = new DOMParser().parseFromString(html, "text/html");
  const walk = (node: Node) => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        const tag = el.tagName.toLowerCase();
        if (tag === "script" || tag === "style" || tag === "iframe" || tag === "object") {
          el.remove();
          continue;
        }
        if (!ALLOWED_TAGS.has(tag)) {
          // Unwrap unknown tags, keep children.
          while (el.firstChild) el.parentNode?.insertBefore(el.firstChild, el);
          el.remove();
          continue;
        }
        const allowed = ALLOWED_ATTRS[tag] ?? new Set<string>();
        for (const attr of Array.from(el.attributes)) {
          const name = attr.name.toLowerCase();
          if (name.startsWith("on") || name === "style" || name === "class") {
            el.removeAttribute(attr.name);
            continue;
          }
          if (!allowed.has(name)) {
            el.removeAttribute(attr.name);
            continue;
          }
          if (name === "href") {
            const normalized = normalizeKenticoHref(attr.value);
            if (!isSafeHref(normalized)) {
              el.removeAttribute(attr.name);
            } else if (normalized !== attr.value) {
              el.setAttribute("href", normalized);
            }
          }
        }
        if (tag === "a") {
          el.setAttribute("rel", "noopener noreferrer");
          el.setAttribute("target", "_blank");
        }
        walk(el);
      }
    }
  };
  walk(doc.body);
  return doc.body.innerHTML;
}

/** Plain-text excerpt for cards / line-clamp (HTML stripped). */
export function htmlToPlainText(value: string): string {
  if (!value) return "";
  if (!looksLikeHtml(value)) return value.trim();
  if (typeof DOMParser === "undefined") {
    return value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
  const doc = new DOMParser().parseFromString(value, "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}
